import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../lang";
import { getProduct, CATEGORIES, MACROS, productsInCategory, getProductPills, isRender, categoryTrail } from "../catalog";
import Breadcrumb from "../components/Breadcrumb";
import ImageLightbox from "../components/ImageLightbox";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import CompareButton from "../components/CompareButton";
import CardGrid from "../components/CardGrid";
import DatasheetLink, { DATASHEETS_ENABLED } from "../components/DatasheetLink";
import SpecSheet from "../components/SpecSheet";
import DatasheetAccess from "../components/DatasheetAccess";
import { familyOf } from "../compare";

function ProductSpecs({ product, l, lang, t, macro }) {
  const wide = macro?.slug === "pergole";
  const apps = l.applications?.length > 0 && (
    <div className="mt-10">
      <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6e6e73] mb-4">{t.applications}</div>
      <div className="flex flex-wrap gap-2.5">
        {l.applications.map((a) => (
          <span key={a} className="px-4 py-2 rounded-full text-[14px] bg-[#f5f5f7] text-[#1d1d1f]">{a}</span>
        ))}
      </div>
    </div>
  );
  return (
    <section className="pt-20 md:pt-28 pb-6 bg-white" data-testid="pdp-specs">
      <div className={`max-w-[1200px] mx-auto px-6 grid grid-cols-1 gap-12 ${wide ? "" : "lg:grid-cols-12 lg:gap-16 items-start"}`}>
        <div className={wide ? "max-w-[820px]" : "lg:col-span-7 lg:pt-4"}>
          <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6e6e73] mb-4">{lang === "it" ? "Il prodotto" : "The product"}</div>
          <h2 className="text-[34px] md:text-[48px] font-semibold tracking-[-0.03em] leading-[1.05] text-[#1d1d1f] mb-6">{l.name}</h2>
          <p className="text-[17px] md:text-[19px] text-[#1d1d1f]/75 leading-[1.65]">{l.description}</p>
          {apps}
        </div>
        <div className={wide ? "" : "lg:col-span-5"}>
          <SpecSheet highlights={product.highlights} lang={lang} title={t.features} wide={wide} />
        </div>
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { productSlug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const product = getProduct(productSlug);
  if (!product) return <Navigate to="/prodotti" replace />;

  const cat = CATEGORIES[product.category];
  const macro = cat ? MACROS[cat.macroSlug] : null;
  const related = productsInCategory(product.category).filter((p) => p.slug !== product.slug).slice(0, 3);

  const l = product[lang];
  const pills = getProductPills(product, lang);

  const galleryImages = (product.gallery || []).filter((src) => src !== product.image);
  // Full list of clickable images for lightbox (hero + gallery, dedup)
  const allImages = [product.image, ...(product.gallery || [])].filter(
    (src, i, arr) => src && arr.indexOf(src) === i
  );
  const openLightbox = (imgUrl) => {
    const idx = allImages.indexOf(imgUrl);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };

  return (
    <main className="min-h-screen bg-white pt-20" data-testid={`product-detail-${productSlug}`}>
      <div className="max-w-[1200px] mx-auto px-6 pt-8">
        <Breadcrumb items={[
          { label: "Home", to: "/" },
          { label: t.productsEyebrow, to: "/prodotti" },
          macro ? { label: macro[lang].title, to: `/prodotti/${macro.slug}` } : null,
          ...categoryTrail(product.category).map((c) => ({ label: c[lang].title, to: `/prodotti/${c.slug}` })),
          { label: l.name },
        ].filter(Boolean)} />
      </div>

      {/* Hero */}
      <section className="pt-8 md:pt-12 pb-0">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          {cat && (
            <div className="text-[13px] uppercase tracking-widest text-[#1D3F94] mb-3" data-testid="pdp-eyebrow">
              {product.slug.startsWith("asas-") ? `ASAŞ · ${cat[lang].title}` : cat[lang].title}
            </div>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-[40px] md:text-[64px] font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.05]"
          >
            {l.name}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 text-[18px] md:text-[22px] text-[#1d1d1f]/85 max-w-[720px] mx-auto"
          >
            {l.tagline}
          </motion.p>
          {pills.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-5 flex flex-wrap items-center justify-center gap-1.5"
              data-testid="pdp-pills"
            >
              {pills.map((p, idx) => (
                <span
                  key={idx}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium tracking-tight ${
                    p.highlight ? "bg-[#1D3F94] text-white" : "bg-[#f5f5f7] text-[#1d1d1f]/80"
                  }`}
                >
                  {p.label}
                </span>
              ))}
            </motion.div>
          )}
          <div className="mt-7 flex justify-center">
            <Link to="/contatti" className="btn-pill btn-primary" data-testid="pdp-cta-quote">
              {t.startProject}
            </Link>
          </div>
          {product.brochure && (
            <div className="mt-5 flex justify-center px-2">
              {DATASHEETS_ENABLED ? (
                <DatasheetLink url={product.brochure} slug={product.slug} name={l.name} testId="pdp-brochure">
                  {t.brochureLabel} <span className="chev">↓</span>
                </DatasheetLink>
              ) : (
                <DatasheetAccess url={product.brochure} />
              )}
            </div>
          )}
          {familyOf(product) && (
            <div className="mt-5 flex justify-center" data-testid="pdp-compare">
              <CompareButton slug={product.slug} variant="inline" />
            </div>
          )}
        </div>

      {/* Before/After slider (optional per product) */}
      {product.beforeAfter && (
        <section className="pb-2 md:pb-6" data-testid="pdp-before-after-section">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-[13px] uppercase tracking-widest text-[#1D3F94]">
                {lang === "it" ? "Prova il confronto" : "Try the comparison"}
              </div>
              <div className="text-[13px] text-[#6e6e73]">
                {lang === "it" ? "Trascina il cursore" : "Drag the handle"}
              </div>
            </div>
            <BeforeAfterSlider
              before={product.beforeAfter.before}
              after={product.beforeAfter.after}
              labelBefore={product.beforeAfter[lang]?.before}
              labelAfter={product.beforeAfter[lang]?.after}
              alt={l.name}
            />
          </div>
        </section>
      )}

      <div className="mt-10 md:mt-12 max-w-[1100px] mx-auto md:px-6">
          {product.video ? (
            <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden" data-testid="pdp-video-wrapper">
              <video
                src={product.video}
                poster={product.image}
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full h-full object-contain bg-black"
                data-testid="pdp-video"
              />
            </div>
          ) : product.youtubeId ? (
            <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden" data-testid="pdp-youtube">
              <iframe
                src={`https://www.youtube.com/embed/${product.youtubeId}?rel=0`}
                title={l.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                frameBorder="0"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => openLightbox(product.image)}
              className={`w-full block relative group rounded-2xl overflow-hidden ${isRender(product.image) ? "bg-white border border-[#e4e4e7]" : "bg-[#f5f5f7]"}`}
              data-testid="pdp-hero-open"
              aria-label={lang === "it" ? "Ingrandisci immagine" : "Enlarge image"}
            >
              {isRender(product.image) ? (
                <div className="w-full aspect-[4/3] md:aspect-auto md:h-[500px] flex items-center justify-center p-6 md:p-12" data-testid="pdp-hero-render">
                  <img src={product.image} alt={l.name} className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-[1.02]" />
                </div>
              ) : (
                <div className="relative w-full aspect-[4/3] md:aspect-auto md:h-[500px]" data-testid="pdp-hero-photo">
                  <img src={product.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" />
                  <img src={product.image} alt={l.name} className="relative w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-[1.02]" />
                </div>
              )}
              <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm text-[#1d1d1f] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </span>
            </button>
          )}
        </div>
      </section>

      {/* Gallery (if extra images) */}
      {galleryImages.length > 0 && (
        <section className="section-pad-sm bg-white" data-testid="pdp-gallery">
          <div className="max-w-[1100px] mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {galleryImages.slice(0, 6).map((src, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => openLightbox(src)}
                  data-testid={`pdp-gallery-item-${i}`}
                  className={`w-[calc(50%-6px)] md:w-[calc((100%-32px)/3)] aspect-[4/3] overflow-hidden rounded-2xl relative group cursor-zoom-in ${isRender(src) ? "bg-white border border-[#e4e4e7]" : "bg-[#f5f5f7]"}`}
                  aria-label={lang === "it" ? "Apri immagine" : "Open image"}
                >
                  {!isRender(src) && <img src={src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" loading="lazy" />}
                  <img src={src} alt="" className={`relative w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-[1.04] ${isRender(src) ? "p-4" : ""}`} loading="lazy" />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm text-[#1d1d1f] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <ImageLightbox
        images={allImages}
        startIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Description + Specs */}
      <ProductSpecs product={product} l={l} lang={lang} t={t} macro={macro} />
      <section className="bg-white pb-16">
        {macro?.slug === "architettonici" && (
          <div className="max-w-[1000px] mx-auto px-6 mt-12 pt-6 border-t border-[#e4e4e7]" data-testid="pdp-disclaimer">
            <p className="text-[12px] leading-relaxed text-[#6e6e73]">
              {lang === "it"
                ? "I valori riportati descrivono le possibilità dei sistemi e possono variare con profili, vetri, ferramenta, dimensioni e tipologia di apertura. Le dimensioni e i pesi massimi richiedono verifica della configurazione. Le certificazioni citate si riferiscono al sistema e al relativo campo di applicazione; le prestazioni del serramento finito vanno definite sulla specifica fornitura. Immagini e dati tecnici: documentazione ufficiale ASAŞ. Le immagini mostrano configurazioni rappresentative."
                : "The values shown describe the systems' possibilities and may vary with profiles, glazing, hardware, dimensions and opening type. Maximum sizes and weights require configuration checks. The certifications mentioned refer to the system and its field of application; the performance of the finished window must be defined on the specific supply. Images and technical data: official ASAŞ documentation. Images show representative configurations."}
            </p>
          </div>
        )}
      </section>
      {related.length > 0 && (
        <section className="section-pad bg-[#f5f5f7]">
          <div className="max-w-[1200px] mx-auto px-6">
            <h2 className="text-[22px] md:text-[28px] font-semibold tracking-[-0.02em] text-[#1d1d1f] mb-8 text-center">
              {lang === "it" ? "Altri prodotti in questa linea" : "More in this line"}
            </h2>
            <CardGrid testId="pdp-related-grid">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/prodotti/p/${p.slug}`}
                  className="card-tile w-full bg-white rounded-3xl overflow-hidden border border-[#e4e4e7]"
                  data-testid={`related-${p.slug}`}
                >
                  <div className={`w-full aspect-[4/3] overflow-hidden ${isRender(p.image) ? "bg-white" : ""}`}>
                    <img src={p.image} alt="" className={`w-full h-full ${isRender(p.image) ? "object-contain object-center p-5" : "object-cover"}`} loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="text-[20px] font-semibold text-[#1d1d1f] mb-1">{p[lang].name}</div>
                    <div className="text-[13px] text-[#6e6e73]">{p[lang].tagline}</div>
                  </div>
                </Link>
              ))}
            </CardGrid>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="section-pad-sm bg-[#0b0f1a] text-white text-center">
        <div className="max-w-[820px] mx-auto px-6">
          <h2 className="text-[28px] md:text-[40px] font-semibold tracking-[-0.02em]">
            {lang === "it" ? "Interessato a" : "Interested in"} {l.name}?
          </h2>
          <p className="mt-3 text-white/80">
            {lang === "it"
              ? "Richiedi un preventivo o prenota un sopralluogo con il nostro team."
              : "Get a quote or book a site visit with our team."}
          </p>
          <div className="mt-6">
            <Link to="/contatti" className="btn-pill btn-primary" data-testid="pdp-bottom-cta">
              {t.startProject}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
